import React, { useState, useEffect } from 'react';
import { Plus, Search, Save, Check, CircleMinus, CircleCheck } from 'lucide-react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import FormControl from '../../common/FormControl';
import Pagination from '../../common/Pagination';
import Toast, { ToastMessage } from '../../common/Toast';
import NoDataFound from '../../common/NoDataFound';
import { confirmWarning, confirmActive } from '../../common/confirm';
import { activeBadgeCss } from '../../../utils/statusVariant';
import Skeleton from '../../common/Skeleton';

interface ControlCategory {
    id: number;
    name: string;
    code: string;
    description: string;
    is_active: boolean;
}

const MOCK_CATEGORIES: ControlCategory[] = [
    { id: 1, name: 'Access Control', code: 'ACC', description: 'Controls related to system and data access.', is_active: true },
    { id: 2, name: 'Change Management', code: 'CHG', description: 'Controls for managing system changes.', is_active: true },
    { id: 3, name: 'Data Integrity', code: 'DAT', description: 'Controls ensuring data accuracy and completeness.', is_active: true },
    { id: 4, name: 'Physical Security', code: 'PHY', description: 'Controls protecting physical assets.', is_active: true },
    { id: 5, name: 'Business Continuity', code: 'BCP', description: 'Controls for disaster recovery and continuity.', is_active: false },
];

const ControlCategoriesMock: React.FC = () => {
    const [categories, setCategories] = useState<ControlCategory[]>(MOCK_CATEGORIES);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('active');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<ControlCategory | null>(null);
    const [formData, setFormData] = useState<Partial<ControlCategory>>({});
    const [submitting, setSubmitting] = useState(false);
    const [toggling, setToggling] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setLoading(false), 400);
        return () => clearTimeout(t);
    }, []);

    useEffect(() => {
        setPage(1);
    }, [searchTerm, statusFilter, pageSize]);

    useEffect(() => {
        setSubmitting(false);
        setToggling(false);
    }, [selectedCategory, showModal]);

    const handleCreate = () => {
        setSelectedCategory(null);
        setFormData({ name: '', code: '', description: '' });
        setIsEditMode(false);
        setShowModal(true);
    };

    const handleEdit = (category: ControlCategory) => {
        setSelectedCategory(category);
        setFormData({ name: category.name, code: category.code, description: category.description });
        setIsEditMode(true);
        setShowModal(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            if (isEditMode && selectedCategory) {
                setCategories(prev => prev.map(c =>
                    c.id === selectedCategory.id ? { ...c, ...formData } as ControlCategory : c
                ));
                setToast({ type: 'success', message: 'Category updated.' });
            } else {
                setCategories(prev => [...prev, { ...formData, id: Date.now(), is_active: true } as ControlCategory]);
                setToast({ type: 'success', message: 'Category created.' });
            }
            setShowModal(false);
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleActive = async () => {
        if (!selectedCategory) return;
        const isActive = selectedCategory.is_active;
        const next = !isActive;
        const confirmFn = isActive ? confirmWarning : confirmActive;
        const ok = await confirmFn({
            title: next ? 'Reactivate this category?' : 'Inactivate this category?',
            text: next
                ? 'It will appear in active lists again.'
                : 'It will be hidden from active lists.',
            confirmText: next ? 'Yes, Reactivate' : 'Yes, Inactivate',
        });
        if (!ok) return;
        setToggling(true);
        try {
            setCategories(prev => prev.map(c =>
                c.id === selectedCategory.id ? { ...c, is_active: next } : c
            ));
            setSelectedCategory(prev => prev ? { ...prev, is_active: next } : prev);
            setToast({ type: next ? 'success' : 'warning', message: next ? 'Category reactivated.' : 'Category inactivated.' });
        } finally {
            setToggling(false);
        }
    };

    const filtered = categories.filter(c => {
        const matchesStatus =
            statusFilter === 'all' ||
            (statusFilter === 'active' ? c.is_active : !c.is_active);
        const q = searchTerm.toLowerCase();
        return matchesStatus && (!q || c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q));
    });

    const totalRecords = filtered.length;
    const paginatedRows = filtered.slice((page - 1) * pageSize, page * pageSize);

    return (
        <div className="netbody-card flex flex-col h-full">
            <div className="netbody-header">
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.SearchSelect
                        value={statusFilter}
                        onChange={v => setStatusFilter(String(v ?? 'active'))}
                        className="filter-status"
                        options={[
                            { value: 'active', label: 'Active' },
                            { value: 'inactive', label: 'Inactive' },
                            { value: 'all', label: 'All' },
                        ]}
                        noSearchIcon
                    />
                    <FormControl.Input
                        type="text"
                        placeholder="Search categories…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                    />
                    <Button variant="fill" icon={Plus} onClick={handleCreate}>Create</Button>
                </div>
            </div>

            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                    <div className="table-list">
                        <div className="table-list-scroll">
                            <table className="table-list-table">
                                <thead>
                                    <tr>
                                        <th className="table-list-th table-list-cell--sno">S.No</th>
                                        <th className="table-list-th">Code</th>
                                        <th className="table-list-th">Name</th>
                                        <th className="table-list-th">Description</th>
                                        <th className="table-list-th">Status</th>
                                    </tr>
                                </thead>
                                <tbody aria-busy={loading} aria-live="polite">
                                    {!loading && filtered.length === 0 && (
                                        <tr>
                                            <td colSpan={5} className="table-list-cell">
                                                <NoDataFound
                                                    title={searchTerm || statusFilter !== 'active' ? 'No Matching Categories' : 'No Categories Found'}
                                                    message={searchTerm || statusFilter !== 'active' ? 'No categories match your search or filter.' : 'Click "Create" to add one.'}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                    {loading && <Skeleton.TableRows cols={['3xs', 'xs', 'md', 'md', 'pill']} />}
                                    {!loading && paginatedRows.map((item, idx) => (
                                        <tr key={item.id} className="table-list-row" onClick={() => handleEdit(item)}>
                                            <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                            <td className="table-list-cell">
                                                <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono font-semibold text-gray-700">{item.code}</code>
                                            </td>
                                            <td className="table-list-cell">
                                                <span className="table-list-cell-title">{item.name}</span>
                                            </td>
                                            <td className="table-list-cell table-list-cell--truncate" title={item.description}>
                                                {item.description}
                                            </td>
                                            <td className="table-list-cell">
                                                <span className={activeBadgeCss(item.is_active)}>
                                                    {item.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalRecords={totalRecords}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            recordLabel="categories"
                        />
                    </div>
                </div>
            </div>

            <Modal isOpen={showModal} onClose={() => setShowModal(false)} size="md">
                <Modal.Header>
                    {isEditMode && selectedCategory
                        ? <>Editing "<span className="item-highlight">{selectedCategory.name}</span>"</>
                        : 'Create Category'}
                </Modal.Header>
                <Modal.Body>
                    <form id="category-form" onSubmit={handleSubmit} className="flex flex-col gap-y-4">
                        <FormControl.Input
                            label="Category Code"
                            required
                            type="text"
                            value={formData.code || ''}
                            onChange={e => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                        />
                        <FormControl.Input
                            label="Category Name"
                            required
                            type="text"
                            value={formData.name || ''}
                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                        />
                        <FormControl.Textarea
                            label="Description"
                            value={formData.description || ''}
                            onChange={e => setFormData({ ...formData, description: e.target.value })}
                            rows={3}
                        />
                    </form>
                </Modal.Body>
                <Modal.Footer className={isEditMode ? 'app-modal-footer--split' : ''}>
                        {isEditMode && selectedCategory && (
                            <Button
                                variant={selectedCategory.is_active ? 'inactive-outline' : 'active-outline'}
                                icon={selectedCategory.is_active ? CircleMinus : CircleCheck}
                                onClick={handleToggleActive}
                                disabled={toggling || submitting}
                            >
                                {toggling ? '...' : selectedCategory.is_active ? 'Inactive' : 'Active'}
                            </Button>
                        )}
                        <Button
                            type="submit"
                            form="category-form"
                            variant={isEditMode ? 'update' : 'fill'}
                            icon={isEditMode ? Check : Save}
                            disabled={submitting || toggling}
                        >
                            {submitting
                                ? isEditMode ? 'Updating...' : 'Submitting...'
                                : isEditMode ? 'Update' : 'Submit'}
                        </Button>
                    </Modal.Footer>
            </Modal>

            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default ControlCategoriesMock;
